import { useMemo, useRef, useState } from 'react';
import { CalendarHeader } from '~/features/shared/calendar/CalendarHeader';
import { CalendarMonthWeekGrid } from '~/features/shared/calendar/CalendarMonthWeekGrid';
import {
  buildDateRange,
  buildMonthGrid,
  buildWeekDays,
  type CalendarViewMode,
  parseISODate,
  startOfMonth,
  toISODate,
} from '~/features/shared/calendar/dateUtils';
import { formatDate, optionLabel, selectionOptionKey } from './availabilityOptionUtils';
import type { PublicAvailabilitySelectionOption } from './schemas';

const monthKey = (date: Date) => date.getFullYear() * 12 + date.getMonth();

/**
 * Month calendar for choosing which of a listing's available dates to
 * negotiate. Only dates that have an option are clickable; a click toggles every
 * option on that date and a shift-click selects the range. The options of every
 * date with a selection are listed underneath so a single shift can be dropped
 * from a multi-shift day.
 */
export const AvailabilityCalendarPicker = ({
  listingId,
  options,
  selectedKeys,
  onToggle,
}: {
  listingId: number;
  options: readonly PublicAvailabilitySelectionOption[];
  selectedKeys: ReadonlySet<string>;
  onToggle: (option: PublicAvailabilitySelectionOption) => void;
}) => {
  const optionsByDate = useMemo(() => {
    const grouped = new Map<string, PublicAvailabilitySelectionOption[]>();
    for (const option of options) {
      grouped.set(option.availabilityDate, [
        ...(grouped.get(option.availabilityDate) ?? []),
        option,
      ]);
    }
    return grouped;
  }, [options]);
  const sortedDates = useMemo(() => [...optionsByDate.keys()].sort(), [optionsByDate]);
  // Navigation is limited to the months that actually hold options.
  const allowedMonths = useMemo(
    () =>
      [...new Set(sortedDates.map((date) => monthKey(parseISODate(date))))].sort((a, b) => a - b),
    [sortedDates]
  );

  const [anchor, setAnchor] = useState(() =>
    startOfMonth(sortedDates.length ? parseISODate(sortedDates[0]) : new Date())
  );
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const lastClicked = useRef<string | null>(null);
  const currentMonth = monthKey(anchor);

  const changeAnchor = (requested: Date) => {
    if (allowedMonths.length === 0) return;
    if (viewMode === 'week') {
      // Weeks may only move within the span of dates that have options.
      const first = parseISODate(sortedDates[0]);
      const last = parseISODate(sortedDates[sortedDates.length - 1]);
      setAnchor(requested < first ? first : requested > last ? last : requested);
      return;
    }
    const wanted = monthKey(requested);
    // Moving on lands on the first allowed month at or after the one asked
    // for, moving back on the last one at or before it; so a gap of several
    // empty months is crossed in one step instead of snapping back.
    const target =
      wanted >= currentMonth
        ? (allowedMonths.find((month) => month >= wanted) ??
          allowedMonths[allowedMonths.length - 1])
        : ([...allowedMonths].reverse().find((month) => month <= wanted) ?? allowedMonths[0]);
    setAnchor(new Date(Math.floor(target / 12), target % 12, 1));
  };

  const changeViewMode = (mode: CalendarViewMode) => {
    setViewMode(mode);
    if (mode === 'month') setAnchor(startOfMonth(anchor));
  };

  const dates = viewMode === 'month' ? buildMonthGrid(anchor) : buildWeekDays(anchor);
  const disabledDates = new Set(
    dates.map(toISODate).filter((isoDate) => !optionsByDate.has(isoDate))
  );
  const isSelected = (option: PublicAvailabilitySelectionOption) =>
    selectedKeys.has(selectionOptionKey(option));
  const fullySelectedDates = new Set(
    sortedDates.filter((date) => (optionsByDate.get(date) ?? []).every(isSelected))
  );

  const handleDateClick = (isoDate: string, { shiftKey }: { shiftKey: boolean }) => {
    const anchorDate = lastClicked.current;
    if (shiftKey && anchorDate) {
      // A range only ever adds: every option on every dated day in it.
      for (const date of buildDateRange(anchorDate, isoDate)) {
        for (const option of optionsByDate.get(date) ?? []) {
          if (!isSelected(option)) onToggle(option);
        }
      }
    } else {
      const onDate = optionsByDate.get(isoDate) ?? [];
      const everySelected = onDate.length > 0 && onDate.every(isSelected);
      for (const option of onDate) {
        if (everySelected === isSelected(option)) onToggle(option);
      }
      lastClicked.current = isoDate;
    }
  };

  const datesWithSelection = sortedDates.filter((date) =>
    (optionsByDate.get(date) ?? []).some(isSelected)
  );

  return (
    <div className="space-y-3" data-cy={`provider-search-calendar-${listingId}`}>
      <CalendarHeader
        anchor={anchor}
        nextDisabled={viewMode === 'month' && !allowedMonths.some((month) => month > currentMonth)}
        onAnchorChange={changeAnchor}
        onViewModeChange={changeViewMode}
        previousDisabled={
          viewMode === 'month' && !allowedMonths.some((month) => month < currentMonth)
        }
        viewMode={viewMode}
      />
      <CalendarMonthWeekGrid
        anchor={anchor}
        dataCyDayPrefix={`provider-search-day-${listingId}`}
        dates={dates}
        disabledDates={disabledDates}
        onDateClick={handleDateClick}
        renderDay={({ isoDate }) => {
          const onDate = optionsByDate.get(isoDate);
          if (!onDate) return null;
          const chosen = onDate.filter(isSelected).length;
          return (
            <span className="text-xs text-content-secondary">
              {chosen > 0 && !fullySelectedDates.has(isoDate)
                ? `${chosen} of ${onDate.length} selected`
                : `${onDate.length} ${onDate.length === 1 ? 'option' : 'options'}`}
            </span>
          );
        }}
        selectedDates={fullySelectedDates}
        viewMode={viewMode}
      />
      <div aria-live="polite" data-cy={`provider-search-selected-${listingId}`}>
        {datesWithSelection.length === 0 ? (
          <p className="text-sm text-content-secondary">
            Choose a highlighted date to select its availability.
          </p>
        ) : (
          <ul className="space-y-3">
            {datesWithSelection.map((date) => (
              <li key={date}>
                <p className="text-sm font-semibold text-content-primary">{formatDate(date)}</p>
                <div className="mt-1 space-y-2">
                  {(optionsByDate.get(date) ?? []).map((option) => {
                    const key = selectionOptionKey(option);
                    return (
                      <label
                        className="flex cursor-pointer gap-3 rounded-md border border-grey-900 p-3 text-sm text-content-secondary hover:border-primary-t-600"
                        key={key}
                      >
                        <input
                          checked={selectedKeys.has(key)}
                          className="mt-0.5"
                          type="checkbox"
                          onChange={() => onToggle(option)}
                        />
                        <span>{optionLabel(option)}</span>
                      </label>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
